import { BOARD_TOOLS, SKIP_TOOLS, followActive, pathOfToolCall, useFollow, viewForPath } from '../state/follow.js';
import { useModel } from '../state/model.js';
import { usePixel } from '../state/pixel.js';
import { useScene } from '../state/scene.js';
import { useSound } from '../state/sound.js';
import { useVideo } from '../state/video.js';
import { useWorkspace, type WorkspaceView } from '../state/workspace.js';
import { eventSocket } from './ws.js';

interface Doc {
  path: string | null;
  saveState: string;
  open: (path: string) => Promise<void>;
}

/** The document store an editor view opens files in (Code opens tabs instead). */
function docStore(view: WorkspaceView): Doc | null {
  switch (view) {
    case 'scene':
      return useScene.getState() as unknown as Doc;
    case 'model':
      return useModel.getState() as unknown as Doc;
    case 'pixel':
      return usePixel.getState() as unknown as Doc;
    case 'sound':
      return useSound.getState() as unknown as Doc;
    case 'video':
      return useVideo.getState() as unknown as Doc;
    default:
      return null;
  }
}

/** Show `path` in its editor (switching view, opening the file there). */
export async function showPath(path: string): Promise<void> {
  const view = viewForPath(path);
  const ws = useWorkspace.getState();
  // Terrain and prefab assets are edited inside the open scene: show the Scene view only.
  const opensDocument = view !== 'scene' || path.toLowerCase().endsWith('.scene.json');
  const doc = docStore(view);
  if (doc && opensDocument && doc.path !== path) {
    // Never switch away from unsaved work.
    if (doc.path && doc.saveState !== 'saved') return;
    if (ws.view !== view) ws.setView(view);
    await doc.open(path);
  } else if (!doc) {
    if (ws.view !== 'code') ws.setView('code');
    await ws.openFile(path);
  } else if (ws.view !== view) {
    ws.setView(view);
  }
}

/** Follow one assistant tool call (on completion, so files are current). */
export async function followToolCall(tool: string, args: Record<string, unknown> | undefined): Promise<void> {
  if (SKIP_TOOLS.has(tool)) return;
  const ws = useWorkspace.getState();
  if (BOARD_TOOLS.has(tool)) {
    if (ws.view !== 'board') ws.setView('board');
    useFollow.getState().setLast(`Board: ${tool.replace(/_/g, ' ')}`);
    return;
  }
  const path = pathOfToolCall(tool, args);
  if (!path) return;
  useFollow.getState().setLast(`${tool.replace(/_/g, ' ')} · ${path}`);
  await showPath(path);
}

/**
 * Starts following the assistant for `projectId`: every completed tool call
 * of the project's agents is shown in its editor while following is on and
 * the project's tab is visible. Returns the stop function.
 */
export function startFollowAssistant(projectId: number): () => void {
  useFollow.getState().load(projectId);
  let queue: Promise<void> = Promise.resolve();
  return eventSocket.subscribe((event) => {
    if (event.type !== 'agent.tool' || event.phase !== 'end' || event.ok === false) return;
    if (useWorkspace.getState().projectId !== projectId || !followActive()) return;
    const { tool, arguments: args } = event;
    // One at a time, in order, so quick bursts land on the last file.
    queue = queue.then(() => followToolCall(tool, args)).catch(() => undefined);
  });
}
